export type SoccerSettlementScoringRule = {
  round_code: string;
  round_name?: string | null;
  round_sequence: number;
  game_win_points?: number;
  round_win_points: number;
};

export type SoccerSettlementRowKey =
  | 'winner'
  | 'runner_up'
  | 'third_place'
  | 'eliminated_semi_final'
  | 'eliminated_quarter_final'
  | 'eliminated_round_16'
  | 'eliminated_round_32'
  | 'missed_knockout';

export type SoccerSettlementRow = {
  key: SoccerSettlementRowKey;
  label: string;
  points: number;
};

export type SoccerWinRow = {
  roundCode: string;
  roundName: string;
  points: number;
};

export type SoccerScoreLadderRow = SoccerWinRow & {
  cumulativePoints: number;
};

export type SoccerScoreLadder = {
  championshipPath: SoccerScoreLadderRow[];
  thirdPlace: SoccerScoreLadderRow | null;
};

const REQUIRED_ROUNDS = [
  'GROUP_STAGE',
  'ROUND_32',
  'ROUND_16',
  'QUARTER_FINAL',
  'SEMI_FINAL',
  'THIRD_PLACE',
  'FINAL',
] as const;

const SOCCER_WIN_ROUND_ORDER = [
  'GROUP_STAGE',
  'ROUND_32',
  'ROUND_16',
  'QUARTER_FINAL',
  'SEMI_FINAL',
  'FINAL',
  'THIRD_PLACE',
] as const;

function roundWinValue(
  rulesByCode: Map<string, SoccerSettlementScoringRule>,
  roundCode: string
): number {
  return rulesByCode.get(roundCode)?.round_win_points ?? 0;
}

export function buildSoccerSettlementRows(
  rules: SoccerSettlementScoringRule[]
): SoccerSettlementRow[] {
  const rulesByCode = new Map(
    rules.map((rule) => [rule.round_code.trim().toUpperCase(), rule])
  );

  if (!REQUIRED_ROUNDS.every((roundCode) => rulesByCode.has(roundCode))) {
    return [];
  }

  const groupStage = roundWinValue(rulesByCode, 'GROUP_STAGE');
  const round32 = roundWinValue(rulesByCode, 'ROUND_32');
  const round16 = roundWinValue(rulesByCode, 'ROUND_16');
  const quarterFinal = roundWinValue(rulesByCode, 'QUARTER_FINAL');
  const semiFinal = roundWinValue(rulesByCode, 'SEMI_FINAL');
  const thirdPlace = roundWinValue(rulesByCode, 'THIRD_PLACE');
  const final = roundWinValue(rulesByCode, 'FINAL');

  const eliminatedRound32 = groupStage;
  const eliminatedRound16 = eliminatedRound32 + round32;
  const eliminatedQuarterFinal = eliminatedRound16 + round16;
  const eliminatedSemiFinal = eliminatedQuarterFinal + quarterFinal;
  const runnerUp = eliminatedSemiFinal + semiFinal;
  const winner = runnerUp + final;
  const thirdPlaceFinish = eliminatedSemiFinal + thirdPlace;

  return [
    { key: 'winner', label: 'Champion', points: winner },
    { key: 'runner_up', label: 'Runner-up', points: runnerUp },
    { key: 'third_place', label: '3rd place Finish', points: thirdPlaceFinish },
    { key: 'eliminated_semi_final', label: 'Eliminated in Semi-Final', points: eliminatedSemiFinal },
    {
      key: 'eliminated_quarter_final',
      label: 'Eliminated in Quarter-Final',
      points: eliminatedQuarterFinal,
    },
    { key: 'eliminated_round_16', label: 'Eliminated in Round of 16', points: eliminatedRound16 },
    { key: 'eliminated_round_32', label: 'Eliminated in Round of 32', points: eliminatedRound32 },
    { key: 'missed_knockout', label: "Don't qualify for Knockout Stage", points: 0 },
  ];
}

export function buildSoccerWinRows(
  rules: SoccerSettlementScoringRule[]
): SoccerWinRow[] {
  const rulesByCode = new Map(
    rules.map((rule) => [rule.round_code.trim().toUpperCase(), rule])
  );

  if (!SOCCER_WIN_ROUND_ORDER.every((roundCode) => rulesByCode.has(roundCode))) {
    return [];
  }

  return SOCCER_WIN_ROUND_ORDER.map((roundCode) => {
    const rule = rulesByCode.get(roundCode);
    return {
      roundCode,
      roundName: rule?.round_name?.trim() || roundCode.replace(/_/g, ' '),
      points: rule?.round_win_points ?? 0,
    };
  });
}

export function buildSoccerScoreLadder(
  rules: SoccerSettlementScoringRule[]
): SoccerScoreLadder {
  const winRows = buildSoccerWinRows(rules);
  const settlementRows = buildSoccerSettlementRows(rules);
  if (!winRows.length || !settlementRows.length) {
    return { championshipPath: [], thirdPlace: null };
  }

  const winRowsByCode = new Map(
    winRows.map((row) => [row.roundCode, row])
  );
  const settlementPointsByKey = new Map(
    settlementRows.map((row) => [row.key, row.points])
  );
  const championshipStages: Array<{
    roundCode: string;
    settlementKey: SoccerSettlementRowKey;
  }> = [
    { roundCode: 'GROUP_STAGE', settlementKey: 'eliminated_round_32' },
    { roundCode: 'ROUND_32', settlementKey: 'eliminated_round_16' },
    { roundCode: 'ROUND_16', settlementKey: 'eliminated_quarter_final' },
    { roundCode: 'QUARTER_FINAL', settlementKey: 'eliminated_semi_final' },
    { roundCode: 'SEMI_FINAL', settlementKey: 'runner_up' },
    { roundCode: 'FINAL', settlementKey: 'winner' },
  ];

  const championshipPath = championshipStages.flatMap((stage) => {
    const winRow = winRowsByCode.get(stage.roundCode);
    const cumulativePoints = settlementPointsByKey.get(stage.settlementKey);
    if (!winRow || cumulativePoints == null) return [];
    return [{ ...winRow, cumulativePoints }];
  });
  const thirdPlaceWin = winRowsByCode.get('THIRD_PLACE');
  const thirdPlacePoints = settlementPointsByKey.get('third_place');

  return {
    championshipPath,
    thirdPlace:
      thirdPlaceWin && thirdPlacePoints != null
        ? { ...thirdPlaceWin, cumulativePoints: thirdPlacePoints }
        : null,
  };
}
